import { NewStockClassDraft, useStockClassList } from '@commenda/api';
import {
  FieldWrapper,
  InputErrorMessage,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import { useEffect, useMemo, useRef, useState } from 'react';
import { STAGED_STOCK_CLASS_ID } from '../constants';
import { isCapitalOnlyLlc, useEntityOwnershipNouns } from '../ownershipNouns';
import { AddShareClassBottomsheet } from './AddShareClassBottomsheet';

interface StockClassFieldProps {
  corporationId: number;
  stockClassId: string | undefined;
  stagedDraft: NewStockClassDraft | null;
  onPickExisting: (id: string | undefined) => void;
  onStageDraft: (draft: NewStockClassDraft) => void;
  onError?: (message: string) => void;
  errorMessage?: string;
}

export const StockClassField = ({
  corporationId,
  stockClassId,
  stagedDraft,
  onPickExisting,
  onStageDraft,
  onError,
  errorMessage,
}: StockClassFieldProps) => {
  const [addOpen, setAddOpen] = useState(false);
  const { data: stockClasses } = useStockClassList(corporationId);
  const { classNouns, classFieldNouns, totalLabel, tracksUnits } =
    useEntityOwnershipNouns(corporationId);

  // An unelected LLC has not chosen a class, so it must not be shown holding one. Dropped
  // once, so a later pick sticks.
  const capitalOnly = isCapitalOnlyLlc(tracksUnits, stockClasses);
  const cleared = useRef(false);
  useEffect(() => {
    if (!capitalOnly || cleared.current || !stockClassId) return;
    cleared.current = true;
    onPickExisting(undefined);
  }, [capitalOnly, stockClassId, onPickExisting]);

  // Only collapse when there is one class to fill in: hidden with none to
  // choose would fail the required check with no way to satisfy it.
  const soleClassId =
    stockClasses?.length === 1 ? stockClasses[0].id : undefined;
  const autoFilled = classNouns === null && soleClassId !== undefined;
  useEffect(() => {
    if (!autoFilled || stockClassId === soleClassId) return;
    onPickExisting(soleClassId);
  }, [autoFilled, soleClassId, stockClassId, onPickExisting]);

  const options = useMemo(() => {
    const existing = (stockClasses ?? []).map((sc) => ({
      label: sc.name,
      value: sc.id,
    }));
    if (stagedDraft) {
      return [
        { label: `${stagedDraft.name} (New)`, value: STAGED_STOCK_CLASS_ID },
        ...existing,
      ];
    }
    return existing;
  }, [stockClasses, stagedDraft]);

  const value =
    stagedDraft != null
      ? STAGED_STOCK_CLASS_ID
      : stockClassId != null
        ? stockClassId
        : '';

  const handlePick = (v: string) => {
    if (!v) {
      onPickExisting(undefined);
      return;
    }
    if (v === STAGED_STOCK_CLASS_ID) return;
    onPickExisting(v);
  };

  if (autoFilled) return null;
  const labels = classFieldNouns;

  return (
    <FieldWrapper>
      <Label required={!capitalOnly}>{labels.singular}</Label>
      <SingleCombobox
        canClearSelection={capitalOnly}
        options={options}
        value={value}
        setValue={handlePick}
        placeholder={`Select or add ${labels.lowerSingular}`}
        addItemConfig={{
          shouldAllowAdd: true,
          onAddItem: () => setAddOpen(true),
          placeholder: `Add ${labels.lowerSingular}`,
        }}
      />
      <AddShareClassBottomsheet
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onStaged={onStageDraft}
        onError={onError}
        classNouns={labels}
        totalLabel={totalLabel}
      />
      {errorMessage && <InputErrorMessage message={errorMessage} />}
    </FieldWrapper>
  );
};
